import { Form } from "antd"
import BaseCheckbox from "../../../components/elements/BaseCheckbox"
import BaseInputArea from "../../../components/elements/BaseInputArea"
import BaseInputField from "../../../components/elements/BaseInputField"
import BaseInputSelection from "../../../components/elements/BaseInputSelection"
import BaseModal from "../../../components/layout/BaseModal"
import { addTaskDataType, addTaskTemplateDataType } from "../types"
import { useContext, useEffect, useState } from "react"
import BaseSelection from "../../../components/elements/BaseSelection"
import dayjs from "dayjs"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useAddTaskMutation, useAddTaskTemplateMutation } from "../api/services"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { GeneralQueryKeys } from "../../../api/constants"
import { getObjectAddressOnly } from "../../Objekte/api/apiCalls"
import { useDelayWhenSearchWithPagination } from "../../../services/functions"

type ChooseEmployeeOrObjectModalType = {
    modalId: number
    queryKey: string
    type: "task" | "taskTemplate"
}

export default function AddTaskModal(props: ChooseEmployeeOrObjectModalType) {

    // OBJECTS and all stuff according to this
    const [searchTermObject, setsearchTermObject] = useState<string>("")
    const searchParamsObject = useDelayWhenSearchWithPagination(searchTermObject)
    const queryKeyObject = GeneralQueryKeys.OBJECTS_ADDRESS_ONLY + searchParamsObject
    const {
        error: allObjectsError,
        isLoading: allObjectsIsLoading,
        data: allObjectsData,
    } = useQuery({
        queryKey: [queryKeyObject],
        queryFn: () => getObjectAddressOnly(searchParamsObject),
    })

    // ADD TASK
    const [addTaskDataTypeSet, setaddTaskDataTypeSet] = useState<addTaskDataType | addTaskTemplateDataType | undefined>()
    const queryClient = useQueryClient()
    const { mutate: addTask } = useAddTaskMutation(queryClient, props.queryKey)
    const { mutate: addTaskTemplate } = useAddTaskTemplateMutation(queryClient, props.queryKey)
    const handleAddTask = () => {
        if (props.type === "task") {
            addTask(addTaskDataTypeSet as addTaskDataType)
        } else if (props.type === "taskTemplate") {
            addTaskTemplate(addTaskDataTypeSet as addTaskTemplateDataType)
        }
        modalCon?.trigger(props.modalId)
        setaddTaskDataTypeSet(undefined)
    }

    // FRONTEND LOGIC
    const [form] = Form.useForm()
    const modalCon = useContext(ModalContext)
    useEffect(() => {
        form.resetFields()
    }, [])
    return (
        <Form
            form={form}
            variant={"outlined"}
            onFinish={() => {
                handleAddTask()
                form.resetFields()
            }}>
            <BaseModal
                modalId={props.modalId}
                title={"Aufgabe erstellen"}
                bottomRow={
                    <div className={`flex justify-end w-full`}>
                        <Form.Item>
                            <button className="btn btn-primary" type="submit">
                                {props.type === "task" ? "Aufgabe erstellen" : "Aufgabenvorlage erstellen"}
                            </button>
                        </Form.Item>
                    </div>
                }>
                <Form.Item className="mb-2" name="object" rules={[{ required: true, message: "Bitte ausfüllen!" }]}>
                    <BaseInputSelection
                        label={"Objekt auswählen"}
                        placeholder={"Auswählen"}
                        data={allObjectsData}
                        classProps={`pt-2`}
                        onSearch={setsearchTermObject}
                        setId={(e) => {
                            form.setFieldsValue({ object: e })
                            setaddTaskDataTypeSet((prev: any) => {
                                return {
                                    ...prev,
                                    object: e,
                                }
                            })
                        }}
                        customLoading={allObjectsIsLoading}
                    />
                </Form.Item>
                <Form.Item className="mb-2" name="activity" rules={[{ required: true, message: "Bitte ausfüllen!" }]}>
                    <BaseInputField
                        label={"Aufgabe"}
                        type={"text"}
                        placeholder="Kehren, Schnee schieben, etc..."
                        onChange={(e: string) => {
                            setaddTaskDataTypeSet((prev: any) => {
                                return {
                                    ...prev,
                                    activity: e,
                                }
                            })
                        }}
                    />
                </Form.Item>

                <Form.Item className="mb-2" name="description" rules={[{ required: false }]}>
                    <BaseInputArea
                        label={"Beschreibung (optional)"}
                        placeholder="Eine detailierte Beschreibung des Auftrags"
                        onChange={(e: string) => {
                            setaddTaskDataTypeSet((prev: any) => {
                                return {
                                    ...prev,
                                        description: e,
                                    }
                                })
                            }
                        }
                    />
                </Form.Item>
                {props.type === "task" && (
                    <Form.Item className="mb-2" name="dueAt" rules={[{ required: true, message: "Bitte ausfüllen!" }]}>
                        <BaseInputField
                            label={"Fällig am"}
                            type={"Date"}
                            min={dayjs().add(1, "day").format("YYYY-MM-DD")}
                            onChange={(e: string) => {
                                setaddTaskDataTypeSet((prev: any) => {
                                    return {
                                        ...prev,
                                        dueAt: e,
                                    }
                                })
                            }}
                        />
                    </Form.Item>
                )}

                <Form.Item className="mb-2" name="estimatedDurationMinutes" rules={[{ required: true, message: "Bitte ausfüllen!" }]}>
                    <BaseInputField
                        label={"Geschätze Zeit in min"}
                        type={"Number"}
                        placeholder="60"
                        onChange={(e: string) => {
                            setaddTaskDataTypeSet((prev: any) => {
                                return {
                                    ...prev,
                                    estimatedDurationMinutes: Number(e),
                                }
                            })
                        }}
                    />
                </Form.Item>

                <div className="flex justify-around">
                    <BaseSelection
                        label={"Besondere Priorität"}
                        placeholder={""}
                        value={
                            addTaskDataTypeSet?.priority === "default"
                                ? "Standard"
                                : addTaskDataTypeSet?.priority === "important"
                                ? "Hoch"
                                : "Standard"
                        }
                        data={["Standard", "Hoch"]}
                        setId={(e: string) => {
                            setaddTaskDataTypeSet((prev: any) => {
                                return {
                                    ...prev,
                                    priority: e === "Hoch" ? "important" : "default",
                                }
                            })
                        }}
                    />
                </div>
                {props.type === "taskTemplate" && (
                    <div className="border-t py-4 mt-4">
                        <label className="pl-2 pt-1">Ausführungsinterval</label>
                        <div className="flex gap-2 w-full pb-3">
                            <p className="pt-5 font-semibold">Ausführung</p>
                            <BaseSelection
                                label={""}
                                placeholder={"X"}
                                value={(addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.interval ?? undefined}
                                classProps="!w-32"
                                data={
                                    (addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.recurrenceType === "week"
                                        ? ["1", "2", "3", "4"]
                                        : (addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.recurrenceType === "month"
                                        ? ["1", "2", "3", "4", "5", "6", "7", "8"]
                                        : ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]
                                }
                                setId={(e: string) => {
                                    setaddTaskDataTypeSet((prev: any) => ({
                                        ...prev,
                                        recurrenceCreationPattern: {
                                            ...prev.recurrenceCreationPattern,
                                            interval: e,
                                        },
                                    }))
                                }}
                            />
                            <p className="pt-5 font-semibold">Mal</p>
                            <p className="pt-5 font-semibold">pro</p>
                            <BaseSelection
                                label={""}
                                placeholder={"Einheit"}
                                data={["Woche", "Monat", "Jahr"]}
                                value={
                                    (addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.recurrenceType === "week"
                                        ? "Woche"
                                        : (addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.recurrenceType === "month"
                                        ? "Monat"
                                        : (addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.recurrenceType === "year"
                                        ? "Jahr"
                                        : undefined
                                }
                                setId={(e: string) => {
                                    setaddTaskDataTypeSet((prev: any) => ({
                                        ...prev,
                                        recurrenceCreationPattern: {
                                            ...prev.recurrenceCreationPattern,
                                            recurrenceType: e === "Woche" ? "week" : e === "Monat" ? "month" : "year",
                                        },
                                    }))
                                }}
                            />
                        </div>
                        <Form.Item className="mb-2" name="appearence" rules={[{ required: true, message: "Bitte ausfüllen!" }]}>
                            <BaseInputField
                                label={"Wie viele Tage vor Fälligkeit soll die Aufgabe angezeigt werden?"}
                                type={"number"}
                                onChange={(e: string) => {
                                    setaddTaskDataTypeSet((prev: any) => ({
                                        ...prev,
                                        recurrenceCreationPattern: {
                                            ...prev.recurrenceCreationPattern,
                                            showDaysBeforeDueDate: Number(e),
                                        },
                                    }))
                                }}
                            />
                        </Form.Item>
                        <div className="flex border-t mt-5 pt-2">
                            <BaseCheckbox
                                check={(addTaskDataTypeSet as addTaskTemplateDataType)?.recurrenceCreationPattern?.endByDate !== undefined}
                                setcheck={(e: boolean) => {
                                    setaddTaskDataTypeSet((prev: any) => ({
                                        ...prev,
                                        recurrenceCreationPattern: {
                                            ...prev.recurrenceCreationPattern,
                                            endByDate: e ? dayjs().endOf("year").toDate() : undefined,
                                        },
                                    }))
                                }}
                                label={"Die Aufgabe gehört zu einer Jahresrechnung und soll zum 31. Dezember diesen Jahres enden"}
                            />
                        </div>
                    </div>
                )}
            </BaseModal>
        </Form>
    )
}
